/**
 * WebSocket Connection Management Plugin
 * Handles WebSocket connections, reconnection logic, and connection state management
 */

export class ConnectionManager {
  constructor(config = {}) {
    this.config = {
      autoReconnect: true,
      connectionTimeout: 10000, // 10 seconds
      maxReconnectAttempts: 5,
      reconnectDelay: 5000, // 5 seconds
      heartbeatInterval: 30000, // 30 seconds
      enableSounds: true,
      ...config
    };

    // Connection state
    this.socket = null;
    this.connectionStatus = 'disconnected'; // 'disconnected', 'connecting', 'connected', 'error'
    this.reconnectAttempts = 0;
    this.connectionStartTime = null;
    this.heartbeatInterval = null;
    
    // Event handlers
    this.eventHandlers = {
      onOpen: null,
      onMessage: null,
      onClose: null,
      onError: null,
      onStatusChange: null
    };

    // Statistics
    this.stats = {
      totalConnections: 0,
      totalDisconnections: 0,
      uptime: 0,
      latency: 0,
      startTime: null
    };
  }

  /**
   * Set event handlers
   */
  setEventHandlers(handlers) {
    this.eventHandlers = { ...this.eventHandlers, ...handlers };
  }

  /**
   * Get current connection status
   */
  getConnectionStatus() {
    return this.connectionStatus;
  }

  /**
   * Get connection statistics
   */
  getStats() {
    return { ...this.stats };
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Connect to WebSocket server
   */
  async connect(url) {
    if (this.connectionStatus === 'connected' || this.connectionStatus === 'connecting') {
      console.warn('Already connected or connecting');
      return false;
    }

    if (!url) {
      throw new Error('WebSocket URL is required');
    }

    try {
      this.connectionStatus = 'connecting';
      this.connectionStartTime = Date.now();
      this._notifyStatusChange('connecting');

      console.log(`🔌 Connecting to ${url}...`);

      // Create WebSocket connection
      this.socket = new WebSocket(url);
      this._setupSocketEvents();

      // Connection timeout
      const timeout = setTimeout(() => {
        if (this.connectionStatus === 'connecting') {
          console.error('❌ Connection timeout');
          this._handleConnectionError(new Error('Connection timeout'));
        }
      }, this.config.connectionTimeout);

      // Wait for connection
      return new Promise((resolve, reject) => {
        const originalOnOpen = this.socket.onopen;
        const originalOnError = this.socket.onerror;

        this.socket.onopen = (event) => {
          clearTimeout(timeout);
          if (originalOnOpen) originalOnOpen(event);
          resolve(true);
        };

        this.socket.onerror = (error) => {
          clearTimeout(timeout);
          if (originalOnError) originalOnError(error);
          reject(error);
        };
      });

    } catch (error) {
      this._handleConnectionError(error);
      throw error;
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (this.socket) {
      this.config.autoReconnect = false; // Prevent auto-reconnect on manual disconnect
      this.socket.close(1000, 'Manual disconnect');
      console.log('🔌 Manually disconnected');
    }
  }

  /**
   * Send message through WebSocket
   */
  sendMessage(data) {
    if (this.connectionStatus !== 'connected' || !this.socket) {
      console.error('❌ Cannot send message: not connected');
      return false;
    }

    try {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      this.socket.send(message);
      console.log('📤 Message sent:', data);
      return true;
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      return false;
    }
  }

  /**
   * Send ping message
   */
  sendPing() {
    const pingTime = Date.now();
    return this.sendMessage({
      type: 'ping',
      payload: { pingTime },
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Send handshake message
   */
  sendHandshake(clientData) {
    return this.sendMessage({
      type: 'handshake',
      payload: {
        ...clientData,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      }
    });
  }

  /**
   * Start heartbeat
   */
  startHeartbeat() {
    this._clearHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.connectionStatus === 'connected') {
        this.sendPing();
      }
    }, this.config.heartbeatInterval);
  }

  /**
   * Clear heartbeat
   */
  _clearHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Setup WebSocket event handlers
   */
  _setupSocketEvents() {
    if (!this.socket) return;

    this.socket.onopen = this._handleConnectionOpen.bind(this);
    this.socket.onmessage = this._handleMessage.bind(this);
    this.socket.onclose = this._handleConnectionClose.bind(this);
    this.socket.onerror = this._handleConnectionError.bind(this);
  }

  /**
   * Handle connection open
   */
  _handleConnectionOpen(event) {
    this.connectionStatus = 'connected';
    this.reconnectAttempts = 0;
    this.stats.totalConnections++;
    this.stats.startTime = Date.now();
    
    const connectTime = Date.now() - this.connectionStartTime;
    console.log(`✅ Connected successfully in ${connectTime}ms`);

    // Start heartbeat
    this.startHeartbeat();

    // Start uptime counter
    this._startUptimeCounter();

    // Notify status change
    this._notifyStatusChange('connected', { connectTime });

    // Call custom handler
    if (this.eventHandlers.onOpen) {
      this.eventHandlers.onOpen(event, { connectTime });
    }
  }

  /**
   * Handle incoming message
   */
  _handleMessage(event) {
    try {
      const startTime = Date.now();
      const rawData = event.data;
      
      // Try to parse as JSON
      let messageData;
      try {
        messageData = JSON.parse(rawData);
      } catch {
        messageData = { content: rawData, raw: true };
      }

      // Calculate latency if timestamp is provided
      let latency = 0;
      if (messageData.timestamp) {
        latency = startTime - new Date(messageData.timestamp).getTime();
      }

      // Create message object
      const message = {
        id: messageData.id || `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: messageData.type || 'message',
        payload: messageData.payload || messageData,
        content: messageData.content || messageData.payload?.message || rawData,
        timestamp: new Date(messageData.timestamp || Date.now()),
        receivedAt: new Date(),
        latency,
        raw: rawData
      };

      // Update latency stats
      this._updateLatencyMetrics(latency);

      console.log('📥 Message received:', message.type);

      // Call custom handler
      if (this.eventHandlers.onMessage) {
        this.eventHandlers.onMessage(message);
      }

    } catch (error) {
      console.error('❌ Failed to process message:', error);
      if (this.eventHandlers.onError) {
        this.eventHandlers.onError(error);
      }
    }
  }

  /**
   * Handle connection close
   */
  _handleConnectionClose(event) {
    const wasConnected = this.connectionStatus === 'connected';
    this.connectionStatus = 'disconnected';
    this.stats.totalDisconnections++;

    // Clear intervals
    this._clearHeartbeat();
    this._stopUptimeCounter();

    let reason = this._getCloseReason(event.code);
    console.log(`🔌 Connection closed: ${reason} (code: ${event.code})`);

    // Notify status change
    this._notifyStatusChange('disconnected', {
      code: event.code,
      reason,
      wasConnected
    });

    // Call custom handler
    if (this.eventHandlers.onClose) {
      this.eventHandlers.onClose(event, { reason, wasConnected });
    }

    // Auto-reconnect if enabled and was previously connected
    if (this.config.autoReconnect && wasConnected && this.reconnectAttempts < this.config.maxReconnectAttempts) {
      this._scheduleReconnect();
    }
  }

  /**
   * Handle connection error
   */
  _handleConnectionError(error) {
    this.connectionStatus = 'error';
    console.error('❌ Connection error:', error);
    
    // Notify status change
    this._notifyStatusChange('error', { error });

    // Call custom handler
    if (this.eventHandlers.onError) {
      this.eventHandlers.onError(error);
    }
  }

  /**
   * Schedule reconnection attempt
   */
  _scheduleReconnect() {
    this.reconnectAttempts++;
    const delay = this.config.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff
    
    console.log(`🔄 Scheduling reconnect attempt ${this.reconnectAttempts}/${this.config.maxReconnectAttempts} in ${delay}ms`);
    
    setTimeout(() => {
      if (this.config.autoReconnect && this.connectionStatus !== 'connected') {
        // Re-attempt connection with last used URL
        if (this._lastUrl) {
          this.connect(this._lastUrl).catch(error => {
            console.error('❌ Reconnection failed:', error);
          });
        }
      }
    }, delay);
  }

  /**
   * Start uptime counter
   */
  _startUptimeCounter() {
    const updateUptime = () => {
      if (this.connectionStatus === 'connected' && this.stats.startTime) {
        this.stats.uptime = Math.floor((Date.now() - this.stats.startTime) / 1000);
        setTimeout(updateUptime, 1000);
      }
    };
    updateUptime();
  }

  /**
   * Stop uptime counter
   */
  _stopUptimeCounter() {
    this.stats.uptime = 0;
    this.stats.startTime = null;
  }

  /**
   * Update latency metrics
   */
  _updateLatencyMetrics(latency) {
    if (latency > 0) {
      // Simple moving average
      this.stats.latency = this.stats.latency ? (this.stats.latency + latency) / 2 : latency;
    }
  }

  /**
   * Get close reason from code
   */
  _getCloseReason(code) {
    const reasons = {
      1000: 'Normal closure',
      1001: 'Going away',
      1002: 'Protocol error',
      1003: 'Unsupported data',
      1006: 'Abnormal closure',
      1011: 'Server error'
    };
    return reasons[code] || 'Unknown reason';
  }

  /**
   * Notify status change
   */
  _notifyStatusChange(status, data = {}) {
    if (this.eventHandlers.onStatusChange) {
      this.eventHandlers.onStatusChange(status, data);
    }
  }

  /**
   * Format uptime
   */
  formatUptime(seconds = this.stats.uptime) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Clean up resources
   */
  cleanup() {
    this.disconnect();
    this._clearHeartbeat();
    this.socket = null;
    this.eventHandlers = {};
  }
}

// Export singleton instance
export const connectionManager = new ConnectionManager();

// Export factory function
export function createConnectionManager(config) {
  return new ConnectionManager(config);
}
